import React, { useEffect, useMemo, useState, ChangeEvent, FC } from 'react';
import JourneyAutocomplete from 'src/common/form/JourneyAutocomplete';
import { formatUrl, isValidUrl } from 'src/utils/url';
import { useDebouncedEffect } from 'src/common/hooks';
import { getCompanies } from './get-companies';
import { classNames } from 'src/cmdk';

export type CompanyInfo = {
  name: string;
  domain: string;
  icon?: string;
};

interface RecipientSearchInputProps {
  onSelectRecipient: (recipient: CompanyInfo) => void;
  onResetRecipient: () => void;
  placeholder?: string;
  onRecipientSearchQueryChange?: (query: string) => void;
  hasSelectedRecipient: boolean;
  autoFocus?: boolean;
  tabindex?: number;
  inputRef?: React.RefObject<HTMLInputElement>;
  initValue?: CompanyInfo;
  inputClassName?: string;
  optionClassName?: string;
  optionsContainerClassName?: string;
  groupClasses?: string;
  groupOptions?: string;
}

export const RecipientSearchInput: FC<RecipientSearchInputProps> = ({
  onSelectRecipient,
  placeholder = "Enter recipient's website URL",
  onResetRecipient,
  onRecipientSearchQueryChange,
  hasSelectedRecipient,
  autoFocus,
  tabindex,
  initValue,
  inputClassName = 'w-full',
  inputRef,
  optionClassName,
  optionsContainerClassName,
  groupClasses,
  groupOptions
}) => {
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [recipientOptions, setRecipientOptions] = useState<CompanyInfo[]>([]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(initValue);

  const visibleRecipientOptions = useMemo(() => {
    return optionsOpen ? recipientOptions : [];
  }, [recipientOptions, optionsOpen]);

  const onRecipientQueryChange = (url: string) => {
    const formattedUrl = formatUrl(url);
    if (isValidUrl(formattedUrl)) {
      const domain = new URL(formattedUrl).hostname;
      const recipient = {
        domain: domain,
        name: '',
        icon: '',
      } as CompanyInfo;
      onSelectRecipient(recipient);
    }
  };

  useEffect(() => {
    if (onRecipientSearchQueryChange) {
      onRecipientSearchQueryChange(recipientSearchQuery);
    }
  }, [recipientSearchQuery]);

  useDebouncedEffect(
    async () => {
      if (recipientSearchQuery) {
        try {
          const {
            companies: domainOptions,
          }: {
            companies: CompanyInfo[];
          } = await getCompanies(recipientSearchQuery);
          // const domainOptions: RecipientDomain[] = await getCompanies(recipientSearchQuery);
          setRecipientOptions(domainOptions);
        } catch (e) {
          //console.error(e);
        }
      } else {
        setRecipientOptions([]);
        onResetRecipient();
        // setLogoUrl(null);
        // setDomain(null);
        // setGeneratedSlug(null);
      }
    },
    250,
    [recipientSearchQuery]
  );

  useEffect(() => {
    return () => {
      setRecipientOptions([]);
      setRecipientSearchQuery('');
      setOptionsOpen(false);
    };
  }, []);

  return (
    <JourneyAutocomplete
      value={internalValue || initValue}
      inputRef={inputRef}
      placeholder={placeholder}
      id='recipient-website'
      filterOptions={(x: CompanyInfo) => x}
      filterSelectedOptions={false}
      freeSolo={false}
      openOnFocus={false}
      onInputChange={(_: ChangeEvent<{}>, newInputValue: string) => {
        setRecipientSearchQuery(newInputValue);
      }}
      onChange={(_: ChangeEvent<{}>, option: CompanyInfo) => {
        if (option) {
          onSelectRecipient(option);
          setInternalValue(option);
        }
      }}
      onPaste={(e: ClipboardEvent) => {
        const pasted = e.clipboardData?.getData('text');
        if (pasted && pasted !== recipientSearchQuery) {
          onRecipientQueryChange(pasted);
        }
      }}
      options={visibleRecipientOptions}
      autoFocus={autoFocus}
      isNeue={true}
      groupClasses={groupClasses}
      groupOptions={groupOptions}
      textInputProps={{
        inputClasses: inputClassName,
        onBlur: (e: any) => {
          const currentValue = e.target.value;
          if (currentValue !== recipientSearchQuery && !hasSelectedRecipient) {
            onRecipientQueryChange(currentValue);
          }
          setOptionsOpen(false);
        },
        onFocus: () => {
          setOptionsOpen(true);
        },
        onKeyDown: (e: any) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            const currentValue = e.target.value;
            onRecipientQueryChange(currentValue);
            if (optionsOpen) {
              setOptionsOpen(false);
            }
          }
        },
      }}
      getOptionLabel={(option: CompanyInfo) => option.domain}
      renderOption={(option: CompanyInfo) => (
        <div
          className={classNames(
            'py-[9px] px-2 text-neue-journey-fg rounded focus:bg-neue-journey-accent-10 active:bg-neue-journey-accent-10',
            optionClassName
          )}
        >
          <div className='text-neue-journey-medium truncate'>{option.domain}</div>
        </div>
      )}
      showEmptyOption={false}
      optionsContainerClassName={optionsContainerClassName}
    />
  );
};
